import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@env/environment';
import { SubscriptionDirective } from '@shared/directives/subscriptions.directive';
import { catchError, EMPTY, interval, startWith, switchMap } from 'rxjs';

@Injectable({
   providedIn: 'root',
})
export class VersionCheckService extends SubscriptionDirective {
   private readonly versionUrl = '/version.json';
   private readonly localStorageKey = 'app_version_hash';

   constructor(
      private http: HttpClient,
      private snackBar: MatSnackBar
   ) {
      super();
   }

   public initVersionCheck(intervalMs = 30000) {
      if (!environment.reloadOnUpdate) {
         return;
      }

      console.log('Reload on new update is enabled.');

      const versionRequest$ = () =>
         this.http.get<{ hash: string; refreshDelay: number }>(this.versionUrl + `?t=${Date.now()}`).pipe(
            catchError((err) => {
               console.error('Version check failed:', err);
               return EMPTY;
            })
         );

      this.subs = interval(intervalMs)
         .pipe(
            startWith(0),
            switchMap(() => versionRequest$())
         )
         .subscribe((response) => {
            if (!response?.hash) {
               console.error('Invalid response for version.json!');
               return;
            }

            const currentHash = localStorage.getItem(this.localStorageKey);

            if (!currentHash) {
               this.saveVersion(response.hash);
            } else if (response.hash !== currentHash) {
               console.log('New version detected. Reloading...');

               const reloadTimeout = setTimeout(
                  () => {
                     this.refreshWithoutCache(response.hash);
                  },
                  (response.refreshDelay ?? 8) * 1000
               );

               if (response.refreshDelay > 0) {
                  const snack = this.snackBar.open(
                     `New update! App will refresh in ${response.refreshDelay} seconds...`,
                     `Don't Refresh now`,
                     {
                        duration: response.refreshDelay * 1000,
                     }
                  );

                  snack.onAction().subscribe(() => {
                     console.warn('User cancelled the update.');
                     clearTimeout(reloadTimeout);
                  });
               }
            }
         });
   }

   private refreshWithoutCache(hash: string) {
      this.saveVersion(hash);

      const url = new URL(window.location.toString());
      url.searchParams.set('version', hash);
      window.location.replace(url.toString());
   }

   private saveVersion(version: string) {
      localStorage.setItem(this.localStorageKey, version);
   }
}
